import Vue from 'vue'
import store from '@/store'
import { allowsAccess } from '@/utils/permissionAccess'
import { hasSafariRole, refundRoles, snackbarOperationsRoles, refundReturnPath } from '@/utils/safariRefundAccess'
import VueRouter from 'vue-router'
import { isLoggedIn } from 'axios-jwt'
import Login from '@/views/Login'
import Access from '@/views/AccessDenied'

// --- Sidebar menu ---------------------------------------------------
import CRM from '@/router/sidebar-menu/crm'
import Clients from '@/router/sidebar-menu/clients'
import Climbing from '@/router/sidebar-menu/climbing'
import Safari from '@/router/sidebar-menu/safari'
import DayTrips from '@/router/sidebar-menu/day-trips'
import IslandVacation from '@/router/sidebar-menu/island-vacation'
import Reservation from '@/router/sidebar-menu/reservation'
import Flights from '@/router/sidebar-menu/flights'
import ReservationDB from '@/router/sidebar-menu/reservation-db'
import ProductStorage from '@/router/sidebar-menu/product-storage'
import EquipmentStorage from '@/router/sidebar-menu/equipment-storage'
import Transfer from '@/router/sidebar-menu/transfer'
import CarManagement from '@/router/sidebar-menu/car-management'
import Employees from '@/router/sidebar-menu/employees'
import HotelManagement from '@/router/sidebar-menu/hotel-management'
import { subscriptionReturnPath } from '@/utils/availabilitySubscriptions'
import Finance from '@/router/sidebar-menu/finance'
import TransferPricing from '@/router/sidebar-menu/transfer-pricing'
import Administrative from '@/router/sidebar-menu/administrative'
import Common from '@/router/sidebar-menu/common'
import Storage from '@/router/sidebar-menu/storage'
import TechSupport from '@/router/sidebar-menu/tech-support'
import GroundOps from '@/router/sidebar-menu/ground-ops'

Vue.use(VueRouter)

let routes = [
  {
    path: '/',
    name: 'base',
    meta: {
      roles: []
    },
  },
  {
    path: '/fingerprint',
    name: 'fingerprint',
    component: () => import('@/views/Fingerprint'),
    meta: {
      title: 'Fingerprint',
      roles: ['general_manager']
    }
  },
  Clients,
  Reservation,
  Flights,
  ReservationDB,
  ProductStorage,
  Transfer,
  CarManagement,
  Employees,
  Storage,
  TechSupport,
  GroundOps,
  {
    path: '/me',
    name: 'me',
    component: () => import('@/views/Me'),
    meta: {
      title: 'My profile',
      roles: []
    },
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      layout: 'welcome',
      title: 'Login',
      roles: []
    },
  },
  {
    path: '/access-denied',
    name: 'access-denied',
    component: Access,
    meta: {
      title: 'Access denied',
      roles: []
    },
  },
  {
    path: '*',
    redirect: { name: 'base' }
  },
]
const alt_routes = [
  CRM,
  Climbing,
  Safari,
  DayTrips,
  IslandVacation,
  EquipmentStorage,
  HotelManagement,
  Finance,
  TransferPricing,
  Administrative,
  Common,
]

if (process.env.VUE_APP_ALARM !== 'true') routes =  [...alt_routes, ...routes]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
})

const checkRoles = (userRoles, routeRoles) => {
  if (!userRoles.length || !routeRoles.length) return true
  return userRoles.some(role => routeRoles.includes(role.code))
}

router.beforeEach(async (to, from, next) => {
  const loggedIn = isLoggedIn()
  const userRoles = JSON.parse(localStorage.getItem('userGroups')) || []

  if (!loggedIn) store.commit('permissions/reset')
  const access = to.meta.access
  if (loggedIn && access) {
    try {
      const profile = await store.dispatch('permissions/refreshProfile')
      const allowed = allowsAccess(access, profile, store.getters['permissions/can'])
      next(allowed ? undefined : { name: 'access-denied' })
    } catch (_) {
      next({ name: 'access-denied' })
    }
    if (to.meta.title) document.title = `${to.meta.title} | Altezza Board`
    return
  }

  if (loggedIn) {
    if (to.name === 'login') next(refundReturnPath(to.query.refund_return) || subscriptionReturnPath(to.query.subscription_return) || { name: userRoles[0]?.index_page || 'base' })
    else if (['safari-snack-bar', 'safari-snack-bar-tour', 'safari-snack-bar-archive'].includes(to.name) && hasSafariRole(userRoles, refundRoles) && !hasSafariRole(userRoles, snackbarOperationsRoles)) next({ name: 'safari-snackbar-refunds', query: to.query })
    else if (to.name === 'access-denied' || checkRoles(userRoles, to.meta.roles)) next()
    else next({ name: 'access-denied' })
  } else {
    if (to.name === 'login') next()
    else {
      const subscription = subscriptionReturnPath(to.fullPath)
      const refund = refundReturnPath(to.fullPath)
      next({ name: 'login', query: refund ? { refund_return: refund } : subscription ? { subscription_return: subscription } : {} })
    }
  }

  if (to.meta.title) document.title = `${to.meta.title} | Altezza Board`
})

export default router
